import { useMemo, useState } from "react";
import { Form, Link, useRouteLoaderData } from "react-router-dom";

import { AsyncFormSelect, SelectOption } from "src/components/common/AsyncSelect";
import { round } from "src/components/common/functions";
import { myOptions } from "src/components/users/UserForm";
import { useAuthStore, User } from "../main/authStore";

import { defaultProposal, defaultProposalLineItem, Proposal } from "./types";

type FormProposal = Omit<Partial<Proposal>, "requester" | "approver" | "data"> & {
    requester?: SelectOption | null;
    approver?: SelectOption | null;
    project?: SelectOption | null;
    data: Proposal["data"];
};

export default function ProposalForm({
    keyword,
}: {
    keyword: "Add" | "Edit";
}) {
    const currentUser = useAuthStore(state => state.user) as User;
    const existing = useRouteLoaderData("Proposal") as { proposal?: Proposal } | undefined;
    const [proposal, setProposal] = useState<FormProposal>(
        existing?.proposal
            ? {
                ...existing.proposal,
                requester: existing.proposal.requester ?? {
                    value: existing.proposal.requesterId,
                    label: existing.proposal.requesterName,
                },
                approver: existing.proposal.approver ?? {
                    value: existing.proposal.approverId,
                    label: existing.proposal.approverName,
                },
                project: existing.proposal.projectId
                    ? { value: String(existing.proposal.projectId), label: existing.proposal.projectName }
                    : null,
                data: existing.proposal.data?.length ? existing.proposal.data : [{ ...defaultProposalLineItem }],
            }
            : {
                ...defaultProposal,
                requester: {
                    value: currentUser.id,
                    label: currentUser.userName,
                },
                project: null,
                approver: null,
                department: currentUser.department,
                data: [{ ...defaultProposalLineItem }],
            }
    );

    const subtotal = useMemo(
        () => proposal.data.reduce((sum, line) => sum + parseAmount(line.lineTotal), 0),
        [proposal.data]
    );
    const tax = parseAmount(proposal.tax);
    const discount = parseAmount(proposal.discount);
    const total = round(subtotal + tax - discount, 2);

    const setLine = (index: number, next: Proposal["data"][number]) => {
        setProposal(prev => ({
            ...prev,
            data: prev.data.map((line, lineIndex) => (lineIndex === index ? next : line)),
        }));
    };

    return (
        <Form method = "post" className = "my-3">
            <input type = "hidden" name = "action" value = {keyword} />
            <input type = "hidden" name = "id" value = {proposal.id ?? ""} />
            <input type = "hidden" name = "data" value = {JSON.stringify(proposal.data)} />
            <input type = "hidden" name = "subtotal" value = {subtotal.toFixed(2)} />
            <input type = "hidden" name = "total" value = {total.toFixed(2)} />
            <div className = "cssSeperateWhenWide ps-3 mb-3">
                <div className = "flex-grow-1 me-3">
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "proposalNumber">Proposal Number:</label>
                        <input
                            required
                            className = "form-control flex-grow-1 ms-3"
                            id = "proposalNumber"
                            name = "proposalNumber"
                            value = {proposal.proposalNumber ?? ""}
                            onChange = {e => setProposal({ ...proposal, proposalNumber: e.currentTarget.value.replace(/\s+/g, "").replace(/\//g, "") })}
                        />
                    </div>
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "proposalDate">Proposal Date:</label>
                        <input
                            required
                            type = "date"
                            min = "1900-01-01"
                            max = "2999-12-31"
                            className = "form-control flex-grow-1 ms-3"
                            id = "proposalDate"
                            name = "proposalDate"
                            value = {proposal.proposalDate ?? ""}
                            onChange = {e => setProposal({ ...proposal, proposalDate: e.currentTarget.value })}
                        />
                    </div>
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "department">Department:</label>
                        <select
                            required
                            className = "form-control flex-grow-1 ms-3"
                            id = "department"
                            name = "department"
                            value = {proposal.department ?? ""}
                            onChange = {e => setProposal({ ...proposal, department: e.currentTarget.value })}
                        >
                            <option value = "">Select</option>
                            {Object.entries(myOptions.department).map(([group, values]) => (
                                <optgroup key = {group} label = {group}>
                                    {(values as string[]).map(value => <option key = {value} value = {value}>{value}</option>)}
                                </optgroup>
                            ))}
                        </select>
                    </div>
                </div>
                <div className = "flex-grow-1 me-3">
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "requesterId">Requester:</label>
                        <div className = "flex-grow-1 ms-3">
                            <AsyncFormSelect
                                required
                                inputId = "requesterId"
                                name = "requesterId"
                                fileName = "common/searchUser.php"
                                placeholder = "Search user…"
                                value = {proposal.requester ?? null}
                                onChange = {next => setProposal({ ...proposal, requester: next as SelectOption | null })}
                            />
                        </div>
                    </div>
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "projectId">Project:</label>
                        <div className = "flex-grow-1 ms-3">
                            <AsyncFormSelect
                                inputId = "projectId"
                                name = "projectId"
                                fileName = "common/searchProject.php"
                                placeholder = "Search project…"
                                value = {proposal.project ?? null}
                                onChange = {next => setProposal({ ...proposal, project: next as SelectOption | null })}
                            />
                        </div>
                    </div>
                    <div className = "d-flex align-items-center mt-2">
                        <label className = "fw-bold text-nowrap mb-0" htmlFor = "approverId">Approver:</label>
                        <div className = "flex-grow-1 ms-3">
                            <AsyncFormSelect
                                required
                                inputId = "approverId"
                                name = "approverId"
                                fileName = "common/searchUser.php"
                                placeholder = "Search user…"
                                value = {proposal.approver ?? null}
                                onChange = {next => setProposal({ ...proposal, approver: next as SelectOption | null })}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className = "px-3 mb-3">
                <label className = "fw-bold mb-2" htmlFor = "notes">Notes:</label>
                <textarea
                    className = "form-control"
                    id = "notes"
                    name = "notes"
                    rows = {4}
                    value = {proposal.notes ?? ""}
                    onChange = {e => setProposal({ ...proposal, notes: e.currentTarget.value })}
                />
            </div>
            <div className = "px-3 mb-3">
                <div className = "d-flex justify-content-between align-items-center mb-2">
                    <div className = "fw-bold">Services</div>
                    <button
                        type = "button"
                        className = "btn btn-outline-primary"
                        onClick = {() => setProposal(prev => ({ ...prev, data: [...prev.data, { ...defaultProposalLineItem }] }))}
                    >
                        Add Line
                    </button>
                </div>
                <div className = "table-responsive">
                    <table className = "table">
                        <thead className = "noTableTopBorder">
                            <tr>
                                <th>Service</th>
                                <th>Description</th>
                                <th>Unit Price</th>
                                <th>Quantity</th>
                                <th>Line Total</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {proposal.data.map((line, index) => (
                                <tr key = {index}>
                                    <td style = {{ minWidth: 260 }}>
                                        <AsyncFormSelect
                                            inputId = {`service-${index}`}
                                            fileName = "common/searchService.php"
                                            placeholder = "Search service…"
                                            value = {line.serviceId ? { value: String(line.serviceId), label: line.serviceName } : null}
                                            onChange = {next => {
                                                const option = next as (SelectOption & { price?: string; notes?: string }) | null;
                                                const quantity = parseAmount(line.quantity || "1");
                                                const unitPrice = parseAmount(option?.price ?? line.unitPrice);
                                                setLine(index, {
                                                    ...line,
                                                    serviceId: option?.value ?? "",
                                                    serviceName: option?.label ?? "",
                                                    description: line.description || option?.notes || "",
                                                    unitPrice: unitPrice.toFixed(2),
                                                    lineTotal: round(unitPrice * quantity, 2).toFixed(2),
                                                });
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            className = "form-control"
                                            value = {line.description}
                                            onChange = {e => setLine(index, { ...line, description: e.currentTarget.value })}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type = "number"
                                            min = "0"
                                            step = "0.01"
                                            className = "form-control"
                                            value = {line.unitPrice}
                                            onChange = {e => {
                                                const unitPrice = parseAmount(e.currentTarget.value);
                                                const quantity = parseAmount(line.quantity);
                                                setLine(index, {
                                                    ...line,
                                                    unitPrice: e.currentTarget.value,
                                                    lineTotal: round(unitPrice * quantity, 2).toFixed(2),
                                                });
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type = "number"
                                            min = "0"
                                            step = "0.01"
                                            className = "form-control"
                                            value = {line.quantity}
                                            onChange = {e => {
                                                const quantity = parseAmount(e.currentTarget.value);
                                                const unitPrice = parseAmount(line.unitPrice);
                                                setLine(index, {
                                                    ...line,
                                                    quantity: e.currentTarget.value,
                                                    lineTotal: round(unitPrice * quantity, 2).toFixed(2),
                                                });
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <input className = "form-control" value = {line.lineTotal} readOnly />
                                    </td>
                                    <td>
                                        <button
                                            type = "button"
                                            className = "btn btn-outline-danger"
                                            disabled = {proposal.data.length === 1}
                                            onClick = {() => setProposal(prev => ({ ...prev, data: prev.data.filter((_, lineIndex) => lineIndex !== index) }))}
                                        >
                                            Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className = "px-3 mb-3">
                <div className = "row g-3">
                    <div className = "col-md-3">
                        <label className = "fw-bold mb-2" htmlFor = "subtotalDisplay">Subtotal</label>
                        <input id = "subtotalDisplay" className = "form-control" value = {subtotal.toFixed(2)} readOnly />
                    </div>
                    <div className = "col-md-3">
                        <label className = "fw-bold mb-2" htmlFor = "tax">Tax</label>
                        <input
                            id = "tax"
                            name = "tax"
                            type = "number"
                            min = "0"
                            step = "0.01"
                            className = "form-control"
                            value = {proposal.tax ?? "0.00"}
                            onChange = {e => setProposal({ ...proposal, tax: e.currentTarget.value })}
                        />
                    </div>
                    <div className = "col-md-3">
                        <label className = "fw-bold mb-2" htmlFor = "discount">Discount</label>
                        <input
                            id = "discount"
                            name = "discount"
                            type = "number"
                            min = "0"
                            step = "0.01"
                            className = "form-control"
                            value = {proposal.discount ?? "0.00"}
                            onChange = {e => setProposal({ ...proposal, discount: e.currentTarget.value })}
                        />
                    </div>
                    <div className = "col-md-3">
                        <label className = "fw-bold mb-2" htmlFor = "totalDisplay">Total</label>
                        <input id = "totalDisplay" className = "form-control" value = {total.toFixed(2)} readOnly />
                    </div>
                </div>
            </div>
            <div className = "my-3 px-3">
                <button type = "submit" className = "btn btn-primary">{keyword}</button>
                <Link to = ".." className = "btn btn-outline-secondary float-end">Back</Link>
            </div>
        </Form>
    );
}

function parseAmount(value: string | number | undefined) {
    const parsed = Number(value ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
}
